import { useEffect, useRef, useState } from "react";
import SEO from "@/components/SEO";
import { motion, useInView } from "framer-motion";
import { MapPin, Mail, Phone, Clock, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useTranslation } from "@/i18n/translations";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AnimatedSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }} transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }} className={className}>{children}</motion.div>
  );
}

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const CONTACT_INFO = [
    { icon: MapPin, label: t("contact.address"), value: "Chemin des Lilas 12, 2503 Biel / Suisse" },
    { icon: Mail, label: t("contact.email"), value: "contact@ayapay.ch" },
    { icon: Phone, label: t("contact.phone"), value: `+41 76 200 36 12` },
    { icon: Clock, label: t("contact.hours"), value: t("contact.hoursVal") },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contact.required"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch {
      toast.error(t("contact.error"));
    }
    setLoading(false);
  };

  return (
    <div data-testid="contact-page">
      <SEO page="contact" />
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="max-w-2xl">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{t("contact.tag")}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A1128] tracking-tight mt-4 leading-[1.1]">{t("contact.title")}</h1>
            <p className="text-base md:text-lg text-[#475569] mt-6 leading-relaxed">{t("contact.desc")}</p>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20">
            <div className="lg:col-span-3">
              <AnimatedSection>
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-[#0A1128] mb-1.5">{t("contact.name")} *</label>
                      <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder={t("contact.namePh")} className="rounded-lg border-slate-200" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A1128] mb-1.5">{t("contact.email")} *</label>
                      <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} placeholder={t("contact.emailPh")} className="rounded-lg border-slate-200" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-[#0A1128] mb-1.5">{t("contact.phone")}</label>
                      <Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} placeholder={t("contact.phonePh")} className="rounded-lg border-slate-200" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A1128] mb-1.5">{t("contact.company")}</label>
                      <Input data-testid="contact-company" value={form.company} onChange={(e) => setForm(p => ({ ...p, company: e.target.value }))} placeholder={t("contact.companyPh")} className="rounded-lg border-slate-200" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A1128] mb-1.5">{t("contact.message")} *</label>
                    <Textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))} placeholder={t("contact.messagePh")} rows={5} className="rounded-lg border-slate-200 resize-none" />
                  </div>
                  <Button type="submit" disabled={loading} data-testid="contact-submit" className="bg-[#00C853] hover:bg-[#00A844] text-white rounded-full px-8 py-3 text-sm font-semibold h-auto">
                    {loading ? t("contact.sending") : t("contact.submit")}
                    <Send size={16} className="ml-2" />
                  </Button>
                </form>
              </AnimatedSection>
            </div>
            <div className="lg:col-span-2">
              <AnimatedSection delay={0.2}>
                <div className="space-y-6">
                  {CONTACT_INFO.map((info, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#1B2B5B]/5 flex items-center justify-center shrink-0">
                        <info.icon size={18} className="text-[#1B2B5B]" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-[#0A1128]">{info.label}</div>
                        <div className="text-sm text-[#475569]">{info.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-10 p-6 bg-[#F8F9FA] rounded-xl border border-slate-100">
                  <h3 className="text-sm font-semibold text-[#0A1128] mb-2">{t("contact.fast")}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed">{t("contact.fastDesc")}</p>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
